class YevoriProductGallery {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[data-media-track]');
    this.counter = root.querySelector('[data-media-current]');
    this.items = this.track ? Array.from(this.track.children) : [];
    this.previousButton = root.querySelector('[data-media-previous]');
    this.nextButton = root.querySelector('[data-media-next]');
    this.dialog = root.querySelector('[data-media-lightbox]');
    this.image = root.querySelector('[data-media-lightbox-image]');
    this.triggers = Array.from(root.querySelectorAll('[data-media-zoom-trigger]'));
    this.activeIndex = Math.max(
      0,
      this.items.findIndex((item) => item.dataset.mediaActive === 'true')
    );
    this.lightboxIndex = 0;
    this.scrollFrame = null;

    if (root.dataset.galleryBound === 'true') return;
    root.dataset.galleryBound = 'true';

    this.bindMediaNavigation();
    this.bindLightbox();
  }

  setNavigationState() {
    const counterText = String(this.activeIndex + 1);
    if (this.counter && this.counter.textContent !== counterText) {
      this.counter.textContent = counterText;
    }

    const atStart = this.activeIndex <= 0;
    const atEnd = this.activeIndex >= this.items.length - 1;
    if (this.previousButton) {
      this.previousButton.disabled = atStart;
      this.previousButton.setAttribute('aria-disabled', String(atStart));
    }
    if (this.nextButton) {
      this.nextButton.disabled = atEnd;
      this.nextButton.setAttribute('aria-disabled', String(atEnd));
    }
  }

  getMediaOffset(index) {
    const firstOffset = this.items[0]?.offsetLeft || 0;
    return (this.items[index]?.offsetLeft || 0) - firstOffset;
  }

  setActiveMedia(index, { scroll = false } = {}) {
    if (!this.items.length) return;
    const nextIndex = Math.min(Math.max(index, 0), this.items.length - 1);
    const changed = nextIndex !== this.activeIndex;
    this.activeIndex = nextIndex;

    this.items.forEach((item, itemIndex) => {
      item.dataset.mediaActive = String(itemIndex === nextIndex);
    });
    this.setNavigationState();

    if (!scroll || !changed) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({
      left: this.getMediaOffset(nextIndex),
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }

  bindMediaNavigation() {
    if (!this.track || !this.items.length) return;

    const updateCounter = () => {
      const trackCenter = this.track.scrollLeft + this.track.clientWidth / 2;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.items.forEach((item, index) => {
        const itemCenter = this.getMediaOffset(index) + item.clientWidth / 2;
        const distance = Math.abs(trackCenter - itemCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      this.setActiveMedia(closestIndex);
    };

    this.track.addEventListener(
      'scroll',
      () => {
        if (this.scrollFrame) return;
        this.scrollFrame = window.requestAnimationFrame(() => {
          updateCounter();
          this.scrollFrame = null;
        });
      },
      { passive: true }
    );

    this.previousButton?.addEventListener('click', () => {
      this.setActiveMedia(this.activeIndex - 1, { scroll: true });
    });
    this.nextButton?.addEventListener('click', () => {
      this.setActiveMedia(this.activeIndex + 1, { scroll: true });
    });
    this.root.addEventListener('product:media-change', (event) => {
      const nextIndex = this.items.findIndex(
        (item) => item.dataset.mediaId === String(event.detail?.mediaId)
      );
      if (nextIndex >= 0) this.setActiveMedia(nextIndex);
    });

    this.setNavigationState();
    if (this.activeIndex > 0) {
      window.requestAnimationFrame(() => {
        this.track.scrollTo({ left: this.getMediaOffset(this.activeIndex), behavior: 'auto' });
      });
    }
  }

  bindLightbox() {
    if (!this.dialog || !this.image || !this.triggers.length) return;

    const close = () => {
      if (typeof this.dialog.close === 'function' && this.dialog.open) {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
      }
      document.body.classList.remove('product-lightbox-open');
    };

    const showImage = (index) => {
      this.lightboxIndex = (index + this.triggers.length) % this.triggers.length;
      const trigger = this.triggers[this.lightboxIndex];
      this.image.src = trigger.dataset.mediaSrc;
      this.image.alt = trigger.dataset.mediaAlt || '';
    };

    const open = (index) => {
      showImage(index);
      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }
      document.body.classList.add('product-lightbox-open');
    };

    this.triggers.forEach((trigger, index) => {
      trigger.addEventListener('click', () => open(index));
    });

    const previous = this.dialog.querySelector('[data-media-lightbox-previous]');
    const next = this.dialog.querySelector('[data-media-lightbox-next]');
    const closeButton = this.dialog.querySelector('[data-media-lightbox-close]');
    const hasMultipleImages = this.triggers.length > 1;

    if (previous) {
      previous.hidden = !hasMultipleImages;
      previous.addEventListener('click', () => showImage(this.lightboxIndex - 1));
    }
    if (next) {
      next.hidden = !hasMultipleImages;
      next.addEventListener('click', () => showImage(this.lightboxIndex + 1));
    }
    if (closeButton) closeButton.addEventListener('click', close);

    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) close();
    });
    this.dialog.addEventListener('close', () => {
      document.body.classList.remove('product-lightbox-open');
      this.image.removeAttribute('src');
    });
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft' && hasMultipleImages) {
        event.preventDefault();
        showImage(this.lightboxIndex - 1);
      }
      if (event.key === 'ArrowRight' && hasMultipleImages) {
        event.preventDefault();
        showImage(this.lightboxIndex + 1);
      }
    });
  }
}

const initYevoriProductGalleries = (container = document) => {
  container.querySelectorAll('[data-product-media-gallery]').forEach((gallery) => {
    new YevoriProductGallery(gallery);
  });
};

document.addEventListener('DOMContentLoaded', () => initYevoriProductGalleries());
document.addEventListener('shopify:section:load', (event) => {
  initYevoriProductGalleries(event.target);
});
